import type { CommonColumnsType, DownloadConfigInfoType } from '../types';
import { formatDateFunc } from '@op/utils/time';

/**
 * 板块 E — 在投广告数分国家
 * data: flatData (xAxis × series 展开的行数据)
 * auxiliaryParams.tableHeader: [{ key, title }]
 */
export const AdsCreativeCountry: DownloadConfigInfoType = {
  columns: ({ filter, auxiliaryParams }) => {
    const { tableHeader = [] } = auxiliaryParams || {};

    function renderTime(item: { [x: string]: any }, key: string | number) {
      return formatDateFunc(filter?.date_type)(item[key]);
    }

    return [
      {
        key: 'time',
        header: 'Time',
        renderHelper: [renderTime],
      },
      ...tableHeader.map(({ key, title }: any) => ({
        key,
        header: title,
      })),
    ];
  },
};

/** 从行数据中收集除 time / id 外的字段顺序（与 WeeklySubTopic 类似） */
function collectTimelineDataKeys(rows: any[]): string[] {
  if (!rows?.length) return [];
  const exclude = new Set(['time', 'id']);
  const seen = new Set<string>();
  const ordered: string[] = [];
  rows.forEach(row => {
    Object.keys(row || {}).forEach(k => {
      if (exclude.has(k) || seen.has(k)) return;
      seen.add(k);
      ordered.push(k);
    });
  });
  return ordered;
}

/**
 * 广告创意概览 - 时间线数据通用下载配置
 * 适用于板块 C、D、E、F 的趋势图下载
 *
 * 列生成（与 WeeklySubTopic 一致：传 data + legend，在 config 里拼列）：
 * - 必传 data：行数组，每行含 time 及各维度 key
 * - auxiliaryParams.legend：数据列 key 顺序（推荐与图表 legend 一致）；不传则从 data 行收集 key
 * - auxiliaryParams.dimensionMap：{ [key]: { en, zh } }，表头优先 zh 再 en
 * - auxiliaryParams.valueNumFmt：数值列 Excel 格式（如 '0.00%' 用于占比）
 * - auxiliaryParams.columnHeaders：{ [key]: string }，按 key 覆盖表头（渠道名、交叉分析列名等）
 * - 兼容：auxiliaryParams.explicitColumns / columns 为 [{ key, header }] 时仍按旧方式拼接
 */
export const AdsOverviewTimeline: DownloadConfigInfoType = {
  columns: (params): CommonColumnsType => {
    const { data, filter, auxiliaryParams } = params;
    const {
      dimensionMap = {},
      columnHeaders = {},
      legend: legendFromParams,
      columns: legacyExplicitColumns,
      explicitColumns,
      valueNumFmt,
    } = auxiliaryParams || {};

    if (!data?.length) return [];

    function renderTime(item: { [x: string]: any }, key: string | number) {
      return formatDateFunc(filter?.date_type)(item[key]);
    }

    const timeColumn = {
      key: 'time',
      header: 'Date',
      renderHelper: [renderTime],
    };

    const legacy = explicitColumns || legacyExplicitColumns;
    if (legacy?.length) {
      return [
        timeColumn,
        ...legacy.map((c: { key: string; header?: string; title?: string }) => ({
          key: c.key,
          header: c.header ?? c.title ?? c.key,
          ...(valueNumFmt ? { numFmt: valueNumFmt } : {}),
        })),
      ];
    }

    const legend: string[] =
      Array.isArray(legendFromParams) && legendFromParams.length
        ? legendFromParams
        : collectTimelineDataKeys(data);

    const dataColumns = legend.map(key => ({
      key,
      header:
        columnHeaders[key] ??
        dimensionMap[key]?.zh ??
        dimensionMap[key]?.en ??
        key,
      ...(valueNumFmt ? { numFmt: valueNumFmt } : {}),
    }));

    return [timeColumn, ...dataColumns];
  },
};
